import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, FileText, User, Printer, Plus, Trash2 } from "lucide-react";
import { usePacientes } from "@/hooks/usePacientes";
import { useFichaClinica, type FichaClinicaInput } from "@/hooks/useFichaClinica";
import { useDentistas } from "@/hooks/useDentistas";
import { useEvolucoesClinicas, type EvolucaoClinicaInput } from "@/hooks/useEvolucoesClinicas";
import { todayLocalISO } from "@/lib/date-utils";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { useClinicaDocumento } from "@/hooks/useClinicaDocumento";
import { clinicBrandHeaderHtml, cloudOdontoSignatureHtml } from "@/lib/documentPrint";

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export function FichaClinica() {
  const { informacoes } = useInformacoesClinica();
  const { clinicaAtual } = useClinicaDocumento(informacoes);
  const { pacientes } = usePacientes();
  const { dentistas } = useDentistas();
  const [selectedPacienteId, setSelectedPacienteId] = useState<string>("");
  const { fichaClinica, isLoading, saveFichaClinica } = useFichaClinica(selectedPacienteId);
  const { evolucoes, createEvolucao, deleteEvolucao } = useEvolucoesClinicas(selectedPacienteId);

  const [formData, setFormData] = useState<FichaClinicaInput>({
    paciente_id: "",
    contato_emergencia_nome: "",
    contato_emergencia_telefone: "",
    contato_emergencia_parentesco: "",
    queixa_principal: "",
    historico_medico: "",
    alergias: "",
    pressao_arterial: "",
    temperatura: "",
    peso: "",
    exame_extraoral: "",
    exame_intraoral: "",
    diagnostico: "",
    plano_tratamento: "",
    observacoes: "",
  });
  const [evolucaoForm, setEvolucaoForm] = useState<EvolucaoClinicaInput>({
    paciente_id: "",
    dentista_id: "",
    data: todayLocalISO(),
    procedimento: "",
    evolucao: "",
    conduta: "",
    retorno: "",
    anexos_texto: "",
  });

  // Load ficha data when it changes
  useEffect(() => {
    if (fichaClinica) {
      setFormData({
        paciente_id: fichaClinica.paciente_id,
        contato_emergencia_nome: fichaClinica.contato_emergencia_nome || "",
        contato_emergencia_telefone: fichaClinica.contato_emergencia_telefone || "",
        contato_emergencia_parentesco: fichaClinica.contato_emergencia_parentesco || "",
        queixa_principal: fichaClinica.queixa_principal || "",
        historico_medico: fichaClinica.historico_medico || "",
        alergias: fichaClinica.alergias || "",
        pressao_arterial: fichaClinica.pressao_arterial || "",
        temperatura: fichaClinica.temperatura || "",
        peso: fichaClinica.peso || "",
        exame_extraoral: fichaClinica.exame_extraoral || "",
        exame_intraoral: fichaClinica.exame_intraoral || "",
        diagnostico: fichaClinica.diagnostico || "",
        plano_tratamento: fichaClinica.plano_tratamento || "",
        observacoes: fichaClinica.observacoes || "",
      });
    } else if (selectedPacienteId) {
      // Reset form for new ficha
      setFormData({
        paciente_id: selectedPacienteId,
        contato_emergencia_nome: "",
        contato_emergencia_telefone: "",
        contato_emergencia_parentesco: "",
        queixa_principal: "",
        historico_medico: "",
        alergias: "",
        pressao_arterial: "",
        temperatura: "",
        peso: "",
        exame_extraoral: "",
        exame_intraoral: "",
        diagnostico: "",
        plano_tratamento: "",
        observacoes: "",
      });
    }
    setEvolucaoForm((prev) => ({ ...prev, paciente_id: selectedPacienteId }));
  }, [fichaClinica, selectedPacienteId]);

  const handlePacienteChange = (pacienteId: string) => {
    setSelectedPacienteId(pacienteId);
  };

  const handleInputChange = (field: keyof FichaClinicaInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!selectedPacienteId) {
      return;
    }
    saveFichaClinica.mutate({ ...formData, paciente_id: selectedPacienteId });
  };

  const selectedPaciente = pacientes.find(p => p.id === selectedPacienteId);

  const handleSaveEvolucao = async () => {
    if (!selectedPacienteId || !evolucaoForm.procedimento || !evolucaoForm.evolucao) return;
    const saved = await createEvolucao({ ...evolucaoForm, paciente_id: selectedPacienteId });
    if (saved) {
      setEvolucaoForm({
        paciente_id: selectedPacienteId,
        dentista_id: "",
        data: todayLocalISO(),
        procedimento: "",
        evolucao: "",
        conduta: "",
        retorno: "",
        anexos_texto: "",
      });
    }
  };

  const handlePrintFicha = () => {
    if (!selectedPaciente) return;
    const popup = window.open("", "_blank", "width=860,height=720");
    if (!popup) return;

    popup.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Ficha Clínica</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 24px; color: #111827; }
            h1 { margin: 0 0 4px; font-size: 24px; }
            h2 { margin: 22px 0 8px; font-size: 16px; border-bottom: 1px solid #d1d5db; padding-bottom: 6px; }
            .muted { color: #6b7280; }
            .grid { display: grid; grid-template-columns: 170px 1fr; gap: 8px 14px; }
            .label { color: #6b7280; font-size: 12px; }
            .value { white-space: pre-wrap; }
            .entry { border: 1px solid #d1d5db; border-radius: 6px; padding: 12px; margin-top: 10px; }
            .no-print { margin-top: 24px; }
            button { padding: 10px 16px; border: 0; border-radius: 4px; background: #111827; color: white; cursor: pointer; }
            @media print { .no-print { display: none; } body { padding: 0; } }
          </style>
        </head>
        <body>
          ${clinicBrandHeaderHtml(clinicaAtual)}
          <h1>Ficha Clínica</h1>
          <div class="muted">Cloud Odonto - Prontuário do paciente</div>

          <h2>Paciente</h2>
          <div class="grid">
            <div class="label">Nome</div><div class="value">${escapeHtml(selectedPaciente.nome)}</div>
            <div class="label">CPF</div><div class="value">${escapeHtml(selectedPaciente.cpf || "-")}</div>
            <div class="label">Telefone</div><div class="value">${escapeHtml(selectedPaciente.telefone || "-")}</div>
            <div class="label">E-mail</div><div class="value">${escapeHtml(selectedPaciente.email || "-")}</div>
          </div>

          <h2>Anamnese e exame</h2>
          <div class="grid">
            <div class="label">Queixa principal</div><div class="value">${escapeHtml(formData.queixa_principal || "-")}</div>
            <div class="label">Histórico médico</div><div class="value">${escapeHtml(formData.historico_medico || "-")}</div>
            <div class="label">Alergias</div><div class="value">${escapeHtml(formData.alergias || "-")}</div>
            <div class="label">Diagnóstico</div><div class="value">${escapeHtml(formData.diagnostico || "-")}</div>
            <div class="label">Plano</div><div class="value">${escapeHtml(formData.plano_tratamento || "-")}</div>
          </div>

          <h2>Evoluções</h2>
          ${evolucoes.length ? evolucoes.map((item) => `
            <div class="entry">
              <strong>${escapeHtml(item.data)} - ${escapeHtml(item.procedimento)}</strong>
              <div class="value">${escapeHtml(item.evolucao)}</div>
              ${item.conduta ? `<div class="muted">Conduta: ${escapeHtml(item.conduta)}</div>` : ""}
              ${item.retorno ? `<div class="muted">Retorno: ${escapeHtml(item.retorno)}</div>` : ""}
            </div>
          `).join("") : `<div class="muted">Nenhuma evolução registrada.</div>`}

          ${cloudOdontoSignatureHtml()}
          <div class="no-print"><button onclick="window.print()">Imprimir</button></div>
        </body>
      </html>
    `);
    popup.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Ficha Clínica</h2>
          <p className="text-sm text-muted-foreground">
            Informações médicas e histórico do paciente
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm"
            variant="outline"
            onClick={handlePrintFicha}
            disabled={!selectedPacienteId}
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          <Button 
            size="sm"
            onClick={handleSave}
            disabled={!selectedPacienteId || saveFichaClinica.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {saveFichaClinica.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>

      {/* Patient Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Selecionar Paciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedPacienteId} onValueChange={handlePacienteChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione um paciente..." />
            </SelectTrigger>
            <SelectContent>
              {pacientes.map((paciente) => (
                <SelectItem key={paciente.id} value={paciente.id}>
                  {paciente.nome} - {paciente.cpf || "Sem CPF"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedPaciente && (
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium">{selectedPaciente.nome}</p>
              <p className="text-sm text-muted-foreground">
                {selectedPaciente.telefone} • {selectedPaciente.email}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedPacienteId && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Dados Pessoais - Read Only */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Dados Pessoais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome Completo</Label>
                    <Input value={selectedPaciente?.nome || ""} disabled />
                  </div>
                  <div>
                    <Label>CPF</Label>
                    <Input value={selectedPaciente?.cpf || ""} disabled />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Data de Nascimento</Label>
                    <Input value={selectedPaciente?.data_nascimento || ""} disabled />
                  </div>
                  <div>
                    <Label>Telefone</Label>
                    <Input value={selectedPaciente?.telefone || ""} disabled />
                  </div>
                </div>
                <div>
                  <Label>Endereço</Label>
                  <Input value={selectedPaciente?.endereco || ""} disabled />
                </div>
              </CardContent>
            </Card>

            {/* Contato de Emergência */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contato de Emergência</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="contato_nome">Nome</Label>
                  <Input 
                    id="contato_nome" 
                    placeholder="Nome do contato"
                    value={formData.contato_emergencia_nome}
                    onChange={(e) => handleInputChange('contato_emergencia_nome', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contato_telefone">Telefone</Label>
                    <Input 
                      id="contato_telefone" 
                      placeholder="(00) 00000-0000"
                      value={formData.contato_emergencia_telefone}
                      onChange={(e) => handleInputChange('contato_emergencia_telefone', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="parentesco">Parentesco</Label>
                    <Input 
                      id="parentesco" 
                      placeholder="Mãe, pai, etc."
                      value={formData.contato_emergencia_parentesco}
                      onChange={(e) => handleInputChange('contato_emergencia_parentesco', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Anamnese */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Anamnese</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="queixa_principal">Queixa Principal</Label>
                <Textarea 
                  id="queixa_principal" 
                  placeholder="Descreva o motivo da consulta"
                  className="min-h-[80px]"
                  value={formData.queixa_principal}
                  onChange={(e) => handleInputChange('queixa_principal', e.target.value)}
                />
              </div>
              
              <Separator />
              
              <div>
                <Label htmlFor="historico_medico">Histórico Médico</Label>
                <Textarea 
                  id="historico_medico" 
                  placeholder="Doenças, cirurgias, medicamentos em uso..."
                  className="min-h-[100px]"
                  value={formData.historico_medico}
                  onChange={(e) => handleInputChange('historico_medico', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="alergias">Alergias</Label>
                <Textarea 
                  id="alergias" 
                  placeholder="Medicamentos, materiais, alimentos..."
                  className="min-h-[60px]"
                  value={formData.alergias}
                  onChange={(e) => handleInputChange('alergias', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Exame Clínico */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Exame Clínico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="pressao">Pressão Arterial</Label>
                  <Input 
                    id="pressao" 
                    placeholder="120/80 mmHg"
                    value={formData.pressao_arterial}
                    onChange={(e) => handleInputChange('pressao_arterial', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="temperatura">Temperatura</Label>
                  <Input 
                    id="temperatura" 
                    placeholder="36.5°C"
                    value={formData.temperatura}
                    onChange={(e) => handleInputChange('temperatura', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="peso">Peso</Label>
                  <Input 
                    id="peso" 
                    placeholder="70 kg"
                    value={formData.peso}
                    onChange={(e) => handleInputChange('peso', e.target.value)}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="exame_extraoral">Exame Extraoral</Label>
                <Textarea 
                  id="exame_extraoral" 
                  placeholder="Simetria facial, palpação, ATM..."
                  className="min-h-[80px]"
                  value={formData.exame_extraoral}
                  onChange={(e) => handleInputChange('exame_extraoral', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="exame_intraoral">Exame Intraoral</Label>
                <Textarea 
                  id="exame_intraoral" 
                  placeholder="Mucosas, língua, dentes, gengiva..."
                  className="min-h-[80px]"
                  value={formData.exame_intraoral}
                  onChange={(e) => handleInputChange('exame_intraoral', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Diagnóstico e Plano de Tratamento */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Diagnóstico e Plano de Tratamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="diagnostico">Diagnóstico</Label>
                <Textarea 
                  id="diagnostico" 
                  placeholder="Diagnóstico clínico..."
                  className="min-h-[80px]"
                  value={formData.diagnostico}
                  onChange={(e) => handleInputChange('diagnostico', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="plano_tratamento">Plano de Tratamento</Label>
                <Textarea 
                  id="plano_tratamento" 
                  placeholder="Procedimentos planejados, sequência de tratamento..."
                  className="min-h-[100px]"
                  value={formData.plano_tratamento}
                  onChange={(e) => handleInputChange('plano_tratamento', e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea 
                  id="observacoes" 
                  placeholder="Informações adicionais..."
                  className="min-h-[60px]"
                  value={formData.observacoes}
                  onChange={(e) => handleInputChange('observacoes', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Evolução Clínica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="evolucao_data">Data</Label>
                  <Input
                    id="evolucao_data"
                    type="date"
                    value={evolucaoForm.data}
                    onChange={(e) => setEvolucaoForm((prev) => ({ ...prev, data: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="evolucao_dentista">Dentista</Label>
                  <Select value={evolucaoForm.dentista_id || "none"} onValueChange={(value) => setEvolucaoForm((prev) => ({ ...prev, dentista_id: value === "none" ? "" : value }))}>
                    <SelectTrigger id="evolucao_dentista">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não informado</SelectItem>
                      {dentistas.map((dentista) => (
                        <SelectItem key={dentista.id} value={dentista.id}>{dentista.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="evolucao_procedimento">Procedimento *</Label>
                  <Input
                    id="evolucao_procedimento"
                    value={evolucaoForm.procedimento}
                    onChange={(e) => setEvolucaoForm((prev) => ({ ...prev, procedimento: e.target.value }))}
                    placeholder="Ex.: Limpeza"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="evolucao_texto">Evolução *</Label>
                <Textarea
                  id="evolucao_texto"
                  className="min-h-[90px]"
                  value={evolucaoForm.evolucao}
                  onChange={(e) => setEvolucaoForm((prev) => ({ ...prev, evolucao: e.target.value }))}
                  placeholder="Registre o que foi realizado e os achados clínicos."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="evolucao_conduta">Conduta</Label>
                  <Textarea
                    id="evolucao_conduta"
                    value={evolucaoForm.conduta || ""}
                    onChange={(e) => setEvolucaoForm((prev) => ({ ...prev, conduta: e.target.value }))}
                    placeholder="Medicação, orientação, encaminhamento..."
                  />
                </div>
                <div>
                  <Label htmlFor="evolucao_retorno">Retorno</Label>
                  <Textarea
                    id="evolucao_retorno"
                    value={evolucaoForm.retorno || ""}
                    onChange={(e) => setEvolucaoForm((prev) => ({ ...prev, retorno: e.target.value }))}
                    placeholder="Prazo e observações para retorno."
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="evolucao_anexos">Anexos / referências</Label>
                <Textarea
                  id="evolucao_anexos"
                  value={evolucaoForm.anexos_texto || ""}
                  onChange={(e) => setEvolucaoForm((prev) => ({ ...prev, anexos_texto: e.target.value }))}
                  placeholder="Descreva arquivos, exames ou links relacionados."
                />
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={handleSaveEvolucao} disabled={!evolucaoForm.procedimento || !evolucaoForm.evolucao}>
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar evolução
                </Button>
              </div>

              <Separator />

              {evolucoes.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma evolução registrada para este paciente.</p>
              ) : (
                <div className="space-y-3">
                  {evolucoes.map((item) => {
                    const dentista = dentistas.find((d) => d.id === item.dentista_id);
                    return (
                      <div key={item.id} className="rounded-md border p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium">{item.data} - {item.procedimento}</p>
                            <p className="text-xs text-muted-foreground">{dentista ? `Dr(a). ${dentista.nome}` : "Dentista não informado"}</p>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => deleteEvolucao(item.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="mt-2 whitespace-pre-wrap text-sm">{item.evolucao}</p>
                        {item.conduta && <p className="mt-2 text-sm text-muted-foreground"><strong>Conduta:</strong> {item.conduta}</p>}
                        {item.retorno && <p className="text-sm text-muted-foreground"><strong>Retorno:</strong> {item.retorno}</p>}
                        {item.anexos_texto && <p className="text-sm text-muted-foreground"><strong>Anexos:</strong> {item.anexos_texto}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {!selectedPacienteId && (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Selecione um paciente para gerenciar a ficha clínica
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
